<template>
    <div class="container fu-page">
        <div class="fu-toolbar">
            <nav v-if="view === 'reminders'" class="fu-filters">
                <button
                    v-for="chip in filterChips"
                    :key="chip.key"
                    type="button"
                    class="fu-chip"
                    :class="{ 'fu-chip--active': filter === chip.key }"
                    @click="filter = chip.key"
                >
                    {{ chip.label }}
                    <span class="fu-chip__count">{{ chip.count }}</span>
                </button>
            </nav>
            <p v-else class="fu-toolbar__hint">{{ hint }}</p>

            <span v-if="loadedAt" class="fu-muted" :title="formatDateTime(loadedAt)">
                {{ __('Updated') }}: {{ fromNow(loadedAt) }}
            </span>
        </div>

        <div v-if="loading && !loadedAt" class="loader loader--chat loader--grey-background">
            <i class="fa fa-spinner fa-spin fa-3x fa-fw" aria-hidden="true"></i>
        </div>

        <template v-else-if="view === 'reminders'">
            <section class="fu-section">
                <h2 class="fu-section__title">
                    {{ __('To contact now') }}
                    <span class="fu-section__count">{{ filteredDue.length }}</span>
                </h2>

                <p v-if="!filteredDue.length" class="fu-empty">{{ __('Nothing to do right now.') }}</p>

                <follow-up-case-card
                    v-for="row in filteredDue"
                    :key="row.id"
                    :case-data="row"
                    context="due"
                    :busy="busyId === row.id"
                    @action="runAction"
                    @note="saveNote"
                ></follow-up-case-card>
            </section>

            <section class="fu-section fu-section--muted">
                <h2 class="fu-section__title">
                    {{ __('Handled – waiting for the next reminder') }}
                    <span class="fu-section__count">{{ filteredScheduled.length }}</span>
                </h2>

                <p v-if="!filteredScheduled.length" class="fu-empty">{{ __('No upcoming reminders.') }}</p>

                <follow-up-case-card
                    v-for="row in filteredScheduled"
                    :key="row.id"
                    :case-data="row"
                    context="scheduled"
                    :busy="busyId === row.id"
                    @action="runAction"
                    @note="saveNote"
                ></follow-up-case-card>
            </section>

            <section v-if="filteredClosed.length" class="fu-section fu-section--muted">
                <button type="button" class="fu-section__toggle" @click="showClosed = !showClosed">
                    <span class="material-icons-round">{{ showClosed ? 'expand_less' : 'expand_more' }}</span>
                    {{ __('Activated or resolved in the last :days days', { days: closedVisibleDays }) }}
                    <span class="fu-section__count">{{ filteredClosed.length }}</span>
                </button>

                <template v-if="showClosed">
                    <follow-up-case-card
                        v-for="row in filteredClosed"
                        :key="'closed-' + row.id"
                        :case-data="row"
                        context="closed"
                        :busy="busyId === row.id"
                        @action="runAction"
                        @note="saveNote"
                    ></follow-up-case-card>
                </template>
            </section>
        </template>

        <template v-else>
            <section class="fu-section">
                <p v-if="!cases.length" class="fu-empty">
                    {{ view === 'complicated' ? __('No complicated cases.') : __('No cancelled cases.') }}
                </p>

                <follow-up-case-card
                    v-for="row in cases"
                    :key="row.id"
                    :case-data="row"
                    :context="view"
                    :busy="busyId === row.id"
                    @action="runAction"
                    @note="saveNote"
                ></follow-up-case-card>

                <VButton
                    v-if="hasMore"
                    :label="loading ? __('loading…') : __('Load more')"
                    :disabled="loading"
                    variant="secondary"
                    size="sm"
                    position="center"
                    @onClick="loadMore"
                />
            </section>
        </template>

        <flash-alert
            v-if="alert.show"
            :key="alert.key"
            :text="alert.text"
            :level="alert.level"
            position="top"
            @closeAlert="alert.show = false"
        ></flash-alert>
    </div>
</template>

<script>
import FollowUpCaseCard from '@components/CoachAdmin/FollowUp/FollowUpCaseCard.vue'
import followUpMixin from '@components/CoachAdmin/FollowUp/followUpMixin'

export default {
    components: {
        FollowUpCaseCard
    },
    mixins: [followUpMixin],
    props: {
        view: {
            type: String,
            default: 'reminders',
            validator(value) {
                return ['reminders', 'complicated', 'cancelled'].indexOf(value) !== -1
            }
        }
    },
    data() {
        return {
            due: [],
            scheduled: [],
            closed: [],
            cases: [],
            page: 1,
            hasMore: false,
            counts: null,
            closedVisibleDays: 30,
            loading: true,
            loadedAt: null,
            busyId: null,
            filter: 'all',
            showClosed: false,
            alert: {
                show: false,
                text: '',
                level: 'success',
                key: 0
            }
        }
    },
    computed: {
        hint() {
            return this.view === 'complicated'
                ? this.__('Cases that need more than a phone call. They stay here until they are cancelled.')
                : this.__('Cancelled cases. Restore a case to move it back to the complicated cases.')
        },
        filterChips() {
            return [
                { key: 'all', label: this.__('All'), count: this.countDue('all') },
                { key: 'prescription', label: this.__('Rezept-Service'), count: this.countDue('prescription') },
                { key: 'registration', label: this.__('Registration'), count: this.countDue('registration') },
                { key: 'expertsconnect', label: 'ExpertsConnect', count: this.countDue('expertsconnect') },
                { key: 'second_diagnosis', label: this.__('2nd diagnosis'), count: this.countDue('second_diagnosis') }
            ]
        },
        filteredDue() {
            return this.due.filter((row) => this.matchesFilter(row))
        },
        filteredScheduled() {
            return this.scheduled.filter((row) => this.matchesFilter(row))
        },
        filteredClosed() {
            return this.closed.filter((row) => this.matchesFilter(row))
        }
    },
    mounted() {
        $(function() {
            $('.item-follow-up').addClass('active')
        })

        this.fetch()
    },
    methods: {
        matchesFilter(row, filter = this.filter) {
            switch (filter) {
                case 'prescription':
                case 'registration':
                    return row.type === filter
                case 'expertsconnect':
                case 'second_diagnosis':
                    return (row.tags || []).indexOf(filter) !== -1
                default:
                    return true
            }
        },
        countDue(filter) {
            return this.due.filter((row) => this.matchesFilter(row, filter)).length
        },
        async fetch() {
            this.loading = true

            try {
                // creating the cases that became due is a write, so it gets its own POST and the
                // data request below stays read-only
                try {
                    await axios.post('/follow-up/sync')
                } catch (error) {
                    // a failed sync only means the lists can be a few minutes stale
                }

                const response = await axios.get('/follow-up/data/' + this.view, {
                    params: { page: this.page }
                })

                if (this.view === 'reminders') {
                    this.due = response.data.data.due
                    this.scheduled = response.data.data.scheduled
                    this.closed = response.data.data.closed
                } else {
                    // the complicated and cancelled lists come in batches
                    this.cases = this.page > 1
                        ? this.cases.concat(response.data.data.cases)
                        : response.data.data.cases
                    this.hasMore = response.data.data.has_more
                }

                this.applyMeta(response.data.meta)
                this.loadedAt = new Date().toISOString()
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Loading failed.')), 'danger')
            }

            this.loading = false
        },
        loadMore() {
            this.page++
            this.fetch()
        },
        applyMeta(meta) {
            if (!meta) {
                return
            }

            this.counts = meta.counts || null

            if (meta.closed_visible_days) {
                this.closedVisibleDays = meta.closed_visible_days
            }

            this.updateNavCounts(meta.counts)
        },
        // the counters in the page sub-navigation are rendered by Blade; keep them in sync after actions
        updateNavCounts(counts) {
            if (!counts) {
                return
            }

            Object.keys(counts).forEach((key) => {
                document.querySelectorAll('[data-follow-up-count="' + key + '"]').forEach((element) => {
                    element.textContent = counts[key]
                })
            })
        },
        async runAction({ caseId, action, type }) {
            if (action === 'cancel' && !window.confirm(this.__('Cancel this case? It will be moved to the cancelled cases.'))) {
                return
            }

            this.busyId = caseId

            try {
                const response = await axios.post('/follow-up/cases/' + caseId + '/action', { action })

                this.applyMeta(response.data.meta)
                // the follow-up moves between sections and pages, so reload from the first batch
                this.page = 1
                await this.fetch()
                this.showAlert(this.actionDoneMessage(action, type))
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('The action failed.')), 'danger')
            }

            this.busyId = null
        },
        async saveNote({ caseId, note, done }) {
            try {
                const response = await axios.put('/follow-up/cases/' + caseId + '/note', { note })

                this.replaceCase(response.data.data)
                this.showAlert(this.__('Note saved.'))
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Saving the note failed.')), 'danger')
            }

            if (typeof done === 'function') {
                done()
            }
        },
        replaceCase(row) {
            const replace = (list) => list.map((current) => (current.id === row.id ? row : current))

            this.due = replace(this.due)
            this.scheduled = replace(this.scheduled)
            this.closed = replace(this.closed)
            this.cases = replace(this.cases)
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data ? error.response.data : {}

            if (data.errors) {
                const first = Object.keys(data.errors)[0]

                if (first && data.errors[first] && data.errors[first][0]) {
                    return data.errors[first][0]
                }
            }

            return data.message || fallback
        },
        showAlert(text, level = 'success') {
            this.alert = {
                show: true,
                text,
                level,
                key: this.alert.key + 1
            }
        }
    }
}
</script>

<style scoped lang="less">
@primary: #5937b7;
@text: #676486;
@heading: #2c2857;
@muted: #b3b1c2;

.fu-page {
    max-width: 1400px;
}

.fu-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin: 24px 0 16px;

    &__hint {
        margin: 0;
        color: @muted;
        font-size: 13px;
    }
}

.fu-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.fu-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    // the action labels are lowercase for the buttons; as filters they read better capitalised
    text-transform: capitalize;
    padding: 6px 14px;
    border: 1px solid #e4e2ee;
    border-radius: 20px;
    background: #fff;
    color: @text;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: @primary;
        color: @primary;
    }

    &--active {
        background: @primary;
        border-color: @primary;
        color: #fff;

        &:hover {
            color: #fff;
        }

        .fu-chip__count {
            background: rgba(255, 255, 255, 0.25);
            color: #fff;
        }
    }

    &__count {
        padding: 0 7px;
        border-radius: 10px;
        background: #f3f3f6;
        color: @text;
        font-size: 12px;
        font-weight: 700;
    }
}

.fu-muted {
    color: @muted;
    font-size: 13px;
}

.fu-section {
    margin-bottom: 32px;

    &__title {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 12px;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: @text;
    }

    &__count {
        padding: 0 7px;
        border-radius: 10px;
        background: #f3f3f6;
        color: @text;
        font-size: 11px;
    }

    &__toggle {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin: 0 0 12px;
        padding: 0;
        border: 0;
        background: none;
        color: @text;
        font-family: inherit;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        cursor: pointer;

        .material-icons-round {
            font-size: 18px;
        }

        &:hover {
            color: @primary;
        }
    }

    &--muted .fu-section__title {
        color: @muted;
    }
}

.fu-empty {
    margin: 0;
    padding: 24px 0;
    color: @muted;
    font-size: 13px;
}
</style>
